import React from "react";

import {
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Loader2,
  Users,
  XCircle,
} from "lucide-react";

import LoadingScreen from "../Utils/LoadingScreen";
import DriverDropdown from "../Utils/DriverDropdown";

// =========================================================
// SLOT MANAGER TABS
// =========================================================

const SlotManagerTabs = ({
  activeTab,

  // =======================================================
  // DRIVER SLOTS TAB
  // =======================================================

  loading,
  paginatedData,
  selectedDate,

  groupedByWindowFull,
  paginatedWindowNames,
  selectedWindow,
  setSelectedWindow,

  getSlotId,
  formatDateTime,

  drivers,
  selectedDriverCodeBySlot,
  setSelectedDriverCodeBySlot,

  saving,
  handleAssignDriver,
  setAssignOpenSlotId,

  totalPages,
  currentPage,
  setCurrentPage,

  // =======================================================
  // CONFIGURE TAB
  // =======================================================

  configLoading,
  filteredConfigs,
  getTeamName,
  handleEditConfig,
}) => {
  return (
    <>
      {/* ================================================= */}
      {/* DRIVER SLOTS TAB */}
      {/* ================================================= */}

      {activeTab === "ordersV2" && (
        <>
          {loading ? (
            <LoadingScreen />
          ) : paginatedData.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-5 py-14 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
                <CalendarDays size={23} />
              </div>

              <p className="mt-3 text-sm font-semibold text-gray-800">
                No driver slots found
              </p>

              <p className="mt-1 text-sm text-gray-500">
                Create a slot or change the selected filters.
              </p>
            </div>
          ) : (
            <>
              {/* SELECTED DATE */}

              <div className="mb-4 flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <CalendarDays size={18} className="text-orange-600" />

                  <span className="text-sm font-semibold text-gray-900">
                    {selectedDate
                      ? new Date(selectedDate).toLocaleDateString("en-US", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "Select a date"}
                  </span>
                </div>
              </div>

              {/* ========================================= */}
              {/* TIME WINDOWS */}
              {/* ========================================= */}

              <div className="mb-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {paginatedWindowNames.map((windowName) => {
                  const slotsInWindow = groupedByWindowFull[windowName] || [];

                  const isOpen = selectedWindow === windowName;

                  const totalDrivers = slotsInWindow.reduce(
                    (sum, slot) =>
                      sum +
                      Number(
                        slot?.currentDrivers ?? slot?.current_drivers ?? 0,
                      ),
                    0,
                  );

                  const totalMax = slotsInWindow.reduce(
                    (sum, slot) =>
                      sum +
                      Number(
                        slot?.maximumDrivers ?? slot?.maximum_drivers ?? 0,
                      ),
                    0,
                  );

                  return (
                    <button
                      key={windowName}
                      type="button"
                      onClick={() =>
                        setSelectedWindow(isOpen ? null : windowName)
                      }
                      className={`flex w-full items-center justify-between rounded-2xl border px-4 py-3.5 text-left shadow-sm transition ${
                        isOpen
                          ? "border-orange-400 bg-orange-50"
                          : "border-gray-200 bg-white hover:border-orange-200 hover:shadow-md"
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-100 text-orange-600">
                          <Clock3 size={17} />
                        </div>

                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {windowName}
                          </p>

                          <p className="text-xs text-gray-500">
                            {slotsInWindow.length} slot
                            {slotsInWindow.length !== 1 ? "s" : ""} ·{" "}
                            {totalDrivers}/{totalMax} drivers
                          </p>
                        </div>
                      </div>

                      <ChevronDown
                        size={18}
                        className={`shrink-0 text-gray-400 transition-transform ${
                          isOpen ? "rotate-180 text-orange-500" : ""
                        }`}
                      />
                    </button>
                  );
                })}
              </div>

              {/* ========================================= */}
              {/* SELECTED WINDOW SLOTS */}
              {/* ========================================= */}

              {selectedWindow && groupedByWindowFull[selectedWindow] && (
                <div className="mt-4 rounded-2xl p-4">
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,300px))] justify-start gap-4">
                    {groupedByWindowFull[selectedWindow].map((slot, index) => {
                      const slotId = getSlotId(slot);

                      const currentDrivers = Number(
                        slot?.currentDrivers ?? slot?.current_drivers ?? 0,
                      );

                      const maximumDrivers = Number(
                        slot?.maximumDrivers ?? slot?.maximum_drivers ?? 0,
                      );

                      const isSlotFull =
                        maximumDrivers > 0 && currentDrivers >= maximumDrivers;

                      return (
                        <article
                          key={`${slotId}-${index}`}
                          className={`w-full max-w-[320px] rounded-2xl border p-4 shadow-sm transition ${
                            isSlotFull
                              ? "cursor-not-allowed border-gray-200 bg-gray-50 opacity-60 grayscale"
                              : "border-gray-200 bg-white hover:border-orange-200 hover:shadow-md"
                          }`}
                        >
                          {/* HEADER */}

                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center justify-between text-xs font-semibold">
                                <div
                                  className={`flex gap-1 rounded-full px-2.5 py-1 ${
                                    isSlotFull
                                      ? "bg-gray-200 text-gray-500"
                                      : "bg-orange-50 text-orange-700"
                                  }`}
                                >
                                  <Clock3 size={15} />

                                  {slot?.windowName || "Time Window"}
                                </div>

                                <p className="mt-0.5 text-xs text-gray-400">
                                  Slot ID: {slot?.slotId || "N/A"}
                                </p>
                              </div>

                              <h3
                                className={`mt-2 line-clamp-2 text-sm font-semibold ${
                                  isSlotFull ? "text-gray-500" : "text-gray-900"
                                }`}
                              >
                                {slot?.fromDate && slot?.toDate
                                  ? `${formatDateTime(
                                      slot.fromDate,
                                    )} - ${formatDateTime(slot.toDate)}`
                                  : "N/A"}
                              </h3>
                            </div>
                          </div>

                          {/* DETAILS */}

                          <div
                            className={`mt-4 space-y-3 rounded-xl p-3 text-sm ${
                              isSlotFull ? "bg-gray-100" : "bg-gray-50"
                            }`}
                          >
                            {/* FROM */}

                            <div className="flex items-start gap-2">
                              <Clock3
                                size={16}
                                className={`mt-0.5 shrink-0 ${
                                  isSlotFull
                                    ? "text-gray-400"
                                    : "text-orange-500"
                                }`}
                              />

                              <div>
                                <p className="text-xs font-medium text-gray-500">
                                  From
                                </p>

                                <p
                                  className={`font-medium ${
                                    isSlotFull
                                      ? "text-gray-500"
                                      : "text-gray-800"
                                  }`}
                                >
                                  {formatDateTime(slot?.fromDate)}
                                </p>
                              </div>
                            </div>

                            {/* TO */}

                            <div className="flex items-start gap-2">
                              <Clock3
                                size={16}
                                className={`mt-0.5 shrink-0 ${
                                  isSlotFull
                                    ? "text-gray-400"
                                    : "text-orange-500"
                                }`}
                              />

                              <div>
                                <p className="text-xs font-medium text-gray-500">
                                  To
                                </p>

                                <p
                                  className={`font-medium ${
                                    isSlotFull
                                      ? "text-gray-500"
                                      : "text-gray-800"
                                  }`}
                                >
                                  {formatDateTime(slot?.toDate)}
                                </p>
                              </div>
                            </div>

                            {/* DRIVER COUNT */}

                            <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-3">
                              <div className="flex items-center gap-2">
                                <Users
                                  size={16}
                                  className={
                                    isSlotFull
                                      ? "text-gray-400"
                                      : "text-blue-600"
                                  }
                                />

                                <span className="text-gray-500">Drivers</span>
                              </div>

                              <span
                                className={`font-semibold ${
                                  isSlotFull ? "text-gray-500" : "text-gray-900"
                                }`}
                              >
                                {currentDrivers}/{maximumDrivers}
                              </span>
                            </div>
                          </div>

                          {/* STATUS */}

                          <div className="mt-4 flex items-center justify-between gap-3">
                            <div
                              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                                isSlotFull
                                  ? "bg-gray-200 text-gray-500"
                                  : slot?.isActive
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                              }`}
                            >
                              {slot?.isActive ? (
                                <CheckCircle2 size={14} />
                              ) : (
                                <XCircle size={14} />
                              )}

                              {slot?.isActive ? "Active" : "Inactive"}
                            </div>

                            {isSlotFull && (
                              <span className="rounded-full bg-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-500">
                                Slot Full
                              </span>
                            )}
                          </div>

                          {/* ================================= */}
                          {/* ASSIGN DRIVER */}
                          {/* ================================= */}

                          <div className="mt-3 border-t border-gray-200 pt-3">
                            <div className="space-y-2">
                              <DriverDropdown
                                drivers={(drivers || []).map((driver) => ({
                                  driverId:
                                    driver.driverCode ||
                                    driver.driverId ||
                                    driver.driver_code ||
                                    driver.driver_id,

                                  fullName: `${
                                    driver.fullName ||
                                    driver.username ||
                                    driver.name ||
                                    "-"
                                  } • ${
                                    driver.teamName || driver.team_name || "-"
                                  }`,
                                }))}
                                value={selectedDriverCodeBySlot[slotId] || ""}
                                onChange={(code) =>
                                  setSelectedDriverCodeBySlot((prev) => ({
                                    ...prev,
                                    [slotId]: code,
                                  }))
                                }
                                placeholder="-- Select --"
                                loading={saving || loading}
                                className="w-full"
                                dropdownHeight="max-h-[172px]"
                                placement="top"
                              />

                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  disabled={saving}
                                  onClick={() => {
                                    setAssignOpenSlotId(null);

                                    setSelectedDriverCodeBySlot((prev) => ({
                                      ...prev,
                                      [slotId]: "",
                                    }));
                                  }}
                                  className="h-9 flex-1 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Cancel
                                </button>

                                <button
                                  type="button"
                                  disabled={
                                    !selectedDriverCodeBySlot[slotId] || saving
                                  }
                                  onClick={() =>
                                    handleAssignDriver(
                                      slot,
                                      selectedDriverCodeBySlot[slotId],
                                    )
                                  }
                                  className="h-9 flex-1 rounded-lg bg-orange-500 text-sm font-medium text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {saving ? (
                                    <span className="flex items-center justify-center gap-1.5">
                                      <Loader2
                                        size={14}
                                        className="animate-spin"
                                      />
                                      Assigning
                                    </span>
                                  ) : (
                                    "Confirm"
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ========================================= */}
              {/* PAGINATION */}
              {/* ========================================= */}

              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-center gap-4">
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((page) => Math.max(page - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Previous
                  </button>

                  <span className="text-sm font-medium text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((page) => Math.min(page + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ================================================= */}
      {/* CONFIGURE SLOT TAB */}
      {/* ================================================= */}

      {activeTab === "configure" && (
        <>
          {configLoading ? (
            <LoadingScreen />
          ) : filteredConfigs.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-5 py-14 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
                <Clock3 size={23} />
              </div>

              <p className="mt-3 text-sm font-semibold text-gray-800">
                No time window configurations found
              </p>

              <p className="mt-1 text-sm text-gray-500">
                Click Configure Slot to create a time window.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
              <table className="w-full min-w-[820px] text-left text-sm">
                <thead className="sticky top-0 z-20 bg-[#2EC8F1] text-white">
                  <tr>
                    <th className="px-5 py-2">Time Window</th>

                    <th className="px-5 py-2">Start Time</th>

                    <th className="px-5 py-2">End Time</th>

                    <th className="px-5 py-2">Team</th>

                    <th className="px-5 py-2">Duration</th>

                    <th className="px-5 py-2">Driver Capacity</th>

                    <th className="px-5 py-2">Status</th>

                    <th className="px-5 py-2 text-right">Action</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {filteredConfigs.map((config, index) => {
                    const isActive =
                      String(config?.isActive) === "1" ||
                      config?.isActive === true ||
                      String(config?.isActive).toLowerCase() === "true";

                    return (
                      <tr
                        key={config?.configId || config?.id || index}
                        className="transition hover:bg-orange-50/40"
                      >
                        <td className="px-2 py-1">
                          <div className="flex items-center gap-2 font-medium text-gray-900">
                            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
                              <Clock3 size={14} />
                            </span>

                            {config?.windowName ||
                              `${config?.startTime || "--:--"} - ${
                                config?.endTime || "--:--"
                              }`}
                          </div>
                        </td>

                        <td className="px-2 py-1 text-gray-700">
                          {config?.startTime || "--:--"}
                        </td>

                        <td className="px-4 py-1 text-gray-700">
                          {config?.endTime || "--:--"}
                        </td>

                        <td className="px-4 py-1 text-gray-700">
                          {getTeamName(config?.teamId)}
                        </td>

                        <td className="px-4 py-1 text-gray-700">
                          {config?.slotDurationMinutes || 0} min
                        </td>

                        <td className="px-4 py-1 text-gray-700">
                          {config?.maxDriverCapacity || 0}
                        </td>

                        <td className="px-4 py-1">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                              isActive
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {isActive ? (
                              <CheckCircle2 size={13} />
                            ) : (
                              <XCircle size={13} />
                            )}

                            {isActive ? "Active" : "Inactive"}
                          </span>
                        </td>

                        <td className="px-4 py-1 text-right">
                          <button
                            type="button"
                            onClick={() => handleEditConfig(config)}
                            className="inline-flex h-7 items-center justify-center gap-1.5 rounded-lg bg-orange-500 px-3 text-xs font-semibold text-white transition hover:bg-orange-600"
                          >
                            Update
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default SlotManagerTabs;
